import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { getFinancialReport, getProjectsProfitability, getEmployeeProductivity } from '../lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  ChartLine,
  TrendUp,
  TrendDown,
  Users,
  Briefcase,
  Clock,
  CurrencyDollar,
  ArrowUp,
  ArrowDown,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const MONTHS_ES = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

export function ReportsPage() {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('financial');
  const [financialData, setFinancialData] = useState(null);
  const [profitData, setProfitData] = useState([]);
  const [productivityData, setProductivityData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, [activeTab, selectedYear]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'financial') {
        const res = await getFinancialReport({ year: parseInt(selectedYear) });
        setFinancialData(res.data);
      } else if (activeTab === 'profitability') {
        const res = await getProjectsProfitability();
        setProfitData(res.data);
      } else if (activeTab === 'productivity') {
        const res = await getEmployeeProductivity();
        setProductivityData(res.data);
      }
    } catch { toast.error(t('errorOccurred')); }
    finally { setLoading(false); }
  };

  const maxIncome = financialData?.months ? Math.max(...financialData.months.map(m => m.income), 1) : 1;

  const tabs = [
    { key: 'financial', label: 'Reporte Financiero', icon: ChartLine },
    { key: 'profitability', label: 'Rentabilidad Proyectos', icon: TrendUp },
    { key: 'productivity', label: 'Productividad Empleados', icon: Users },
  ];

  return (
    <Layout title="Reportes">
      <div className="space-y-6" data-testid="reports-page">
        {/* Tabs */}
        <div className="flex gap-2 flex-wrap">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                  activeTab === tab.key ? 'bg-[#E2B05E] text-[#0B0E14]' : 'bg-[#151B26] text-[#9CA3AF] hover:text-white border border-[#242E42]'}`}
                data-testid={`tab-${tab.key}`}>
                <Icon className="w-4 h-4" />{tab.label}
              </button>
            );
          })}
        </div>

        {/* Financial Report */}
        {activeTab === 'financial' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="font-['Outfit'] text-lg font-semibold text-[#F3F4F6]">Reporte Financiero Anual</h3>
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-32 valhalla-input"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#151B26] border-[#242E42]">
                  {[2024,2025,2026,2027].map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {financialData && (
              <>
                {/* Summary */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="valhalla-card p-5 border-emerald-500/30">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 rounded-lg bg-emerald-500/10 text-emerald-400"><ArrowUp weight="bold" className="w-5 h-5" /></div>
                      <div><p className="text-xs text-[#9CA3AF] uppercase">Ingresos Totales</p><p className="font-['Outfit'] text-2xl font-bold text-emerald-400">${financialData.totals.income.toLocaleString()}</p></div>
                    </div>
                  </div>
                  <div className="valhalla-card p-5 border-red-500/30">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 rounded-lg bg-red-500/10 text-red-400"><ArrowDown weight="bold" className="w-5 h-5" /></div>
                      <div><p className="text-xs text-[#9CA3AF] uppercase">Gastos Totales</p><p className="font-['Outfit'] text-2xl font-bold text-red-400">${financialData.totals.expenses.toLocaleString()}</p></div>
                    </div>
                  </div>
                  <div className="valhalla-card p-5 border-[#E2B05E]/30">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 rounded-lg bg-[#E2B05E]/10 text-[#E2B05E]"><CurrencyDollar weight="bold" className="w-5 h-5" /></div>
                      <div><p className="text-xs text-[#9CA3AF] uppercase">Utilidad Neta</p><p className={`font-['Outfit'] text-2xl font-bold ${financialData.totals.profit >= 0 ? 'text-[#E2B05E]' : 'text-red-400'}`}>${financialData.totals.profit.toLocaleString()}</p></div>
                    </div>
                  </div>
                </div>

                {/* Monthly Chart */}
                <div className="valhalla-card p-6">
                  <h4 className="font-semibold text-[#F3F4F6] mb-6">Tendencia Mensual {selectedYear}</h4>
                  <div className="space-y-3">
                    {financialData.months.map((m) => (
                      <div key={m.month} className="grid grid-cols-12 gap-3 items-center text-sm">
                        <span className="col-span-1 text-[#9CA3AF] font-medium">{m.month_name}</span>
                        <div className="col-span-4 flex items-center gap-2">
                          <div className="flex-1 h-5 bg-[#0B0E14] rounded-sm overflow-hidden">
                            <div className="h-full bg-emerald-500/60 rounded-sm" style={{ width: `${Math.max(2, (m.income / maxIncome) * 100)}%` }} />
                          </div>
                          <span className="text-emerald-400 w-20 text-right">${m.income.toLocaleString()}</span>
                        </div>
                        <div className="col-span-4 flex items-center gap-2">
                          <div className="flex-1 h-5 bg-[#0B0E14] rounded-sm overflow-hidden">
                            <div className="h-full bg-red-500/60 rounded-sm" style={{ width: `${maxIncome > 0 ? Math.max(2, (m.expenses / maxIncome) * 100) : 2}%` }} />
                          </div>
                          <span className="text-red-400 w-20 text-right">${m.expenses.toLocaleString()}</span>
                        </div>
                        <span className={`col-span-3 text-right font-bold ${m.profit >= 0 ? 'text-[#E2B05E]' : 'text-red-400'}`}>${m.profit.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-6 mt-4 pt-4 border-t border-[#242E42] text-xs text-[#9CA3AF]">
                    <span className="flex items-center gap-2"><span className="w-3 h-3 bg-emerald-500/60 rounded-sm" />Ingresos</span>
                    <span className="flex items-center gap-2"><span className="w-3 h-3 bg-red-500/60 rounded-sm" />Gastos</span>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Project Profitability */}
        {activeTab === 'profitability' && (
          <div className="space-y-4">
            <h3 className="font-['Outfit'] text-lg font-semibold text-[#F3F4F6]">Rentabilidad por Proyecto</h3>
            {profitData.length > 0 ? (
              <div className="valhalla-card overflow-hidden">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Proyecto</th>
                      <th>Cliente</th>
                      <th>Presupuesto</th>
                      <th>Ingresos</th>
                      <th>Costo</th>
                      <th>Utilidad</th>
                      <th>Margen</th>
                      <th>Horas</th>
                      <th>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {profitData.map((p) => (
                      <tr key={p.project_id}>
                        <td className="text-[#F3F4F6] font-medium">{p.name}</td>
                        <td className="text-[#9CA3AF]">{p.client_name || '-'}</td>
                        <td className="text-[#9CA3AF]">${p.budget.toLocaleString()}</td>
                        <td className="text-emerald-400 font-bold">${p.revenue.toLocaleString()}</td>
                        <td className="text-red-400">${p.cost.toLocaleString()}</td>
                        <td className={`font-bold ${p.profit >= 0 ? 'text-[#E2B05E]' : 'text-red-400'}`}>${p.profit.toLocaleString()}</td>
                        <td><Badge className={p.margin >= 20 ? 'bg-emerald-500/20 text-emerald-400' : p.margin >= 0 ? 'bg-amber-500/20 text-amber-400' : 'bg-red-500/20 text-red-400'}>{p.margin}%</Badge></td>
                        <td className="text-[#9CA3AF]">{p.total_hours.toFixed(1)}h / {p.estimated_hours}h</td>
                        <td><Badge className={p.status === 'activo' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-gray-500/20 text-gray-400'}>{p.status}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="valhalla-card p-12 text-center"><Briefcase className="w-12 h-12 mx-auto text-[#9CA3AF] mb-4" /><p className="text-[#9CA3AF]">No hay proyectos con datos suficientes</p></div>
            )}
          </div>
        )}

        {/* Employee Productivity */}
        {activeTab === 'productivity' && (
          <div className="space-y-4">
            <h3 className="font-['Outfit'] text-lg font-semibold text-[#F3F4F6]">Productividad de Empleados (Mes Actual)</h3>
            {productivityData.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {productivityData.map((emp) => (
                  <div key={emp.employee_id} className="valhalla-card p-5">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h4 className="font-semibold text-[#F3F4F6]">{emp.name}</h4>
                        <p className="text-sm text-[#9CA3AF]">{emp.position} | {emp.level}</p>
                      </div>
                      <Badge className={emp.utilization >= 80 ? 'bg-emerald-500/20 text-emerald-400' : emp.utilization >= 50 ? 'bg-amber-500/20 text-amber-400' : 'bg-red-500/20 text-red-400'}>
                        {emp.utilization}%
                      </Badge>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-[#9CA3AF] flex items-center gap-1"><Clock className="w-3 h-3" /> Horas</span>
                          <span className="text-[#F3F4F6]">{emp.total_hours.toFixed(1)}h / {emp.monthly_capacity}h</span>
                        </div>
                        <Progress value={emp.utilization} className="h-2 bg-[#242E42]" />
                      </div>
                      <div className="grid grid-cols-3 gap-3 text-center pt-2 border-t border-[#242E42]">
                        <div><p className="text-lg font-bold text-[#3AB8D6]">{emp.billable_pct}%</p><p className="text-xs text-[#9CA3AF]">Facturable</p></div>
                        <div><p className="text-lg font-bold text-emerald-400">{emp.tasks_completed}</p><p className="text-xs text-[#9CA3AF]">Completadas</p></div>
                        <div><p className="text-lg font-bold text-amber-400">{emp.tasks_in_progress}</p><p className="text-xs text-[#9CA3AF]">En Progreso</p></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="valhalla-card p-12 text-center"><Users className="w-12 h-12 mx-auto text-[#9CA3AF] mb-4" /><p className="text-[#9CA3AF]">No hay datos de productividad</p></div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}

export default ReportsPage;
